import { Banknote, CreditCard, ShoppingCart, Undo2 } from 'lucide-react'
import type { KeyboardEvent, ReactNode } from 'react'

import { formatAmount } from './money'
import { ProductThumb } from './PhotoViewer'
import './receipt-table.css'

const money = (value: number | null) => formatAmount({ rubles: value })

/** Вид оплаты чека по разбивке: обе части больше нуля — смешанная; разбивки нет — неизвестно, а не «наличные». */
export type ReceiptPaymentKind = 'cash' | 'card' | 'mixed' | 'unknown'
export function paymentKindOf(cash: number | null, card: number | null): ReceiptPaymentKind {
  if (cash === null || card === null) return 'unknown'
  if (cash > 0 && card > 0) return 'mixed'
  if (card > 0) return 'card'
  if (cash > 0) return 'cash'
  return 'unknown'
}
const PAYMENT_TEXT: Record<ReceiptPaymentKind, string> = { cash: 'Наличные', card: 'Карта', mixed: 'Смешанная', unknown: '—' }

/** Вид оплаты значком и словом (не только цветом); у смешанной в подсказке — обе части. */
export function ReceiptPaymentKindText({ cash, card }: { cash: number | null; card: number | null }) {
  const kind = paymentKindOf(cash, card)
  const title = kind === 'mixed' ? `Наличными ${money(cash)} · картой ${money(card)}` : kind === 'unknown' ? 'Разбивка оплаты не указана' : PAYMENT_TEXT[kind]
  return <span className="krp-receipts__pay" title={title}>
    {(kind === 'cash' || kind === 'mixed') && <Banknote size={14} aria-hidden="true" />}
    {(kind === 'card' || kind === 'mixed') && <CreditCard size={14} aria-hidden="true" />}
    {PAYMENT_TEXT[kind]}
  </span>
}

/** Нажатие пришло с кнопки или ссылки внутри строки (возврат, переход): у неё своё действие, окно чека не открывается. */
const fromControl = (target: EventTarget) => target instanceof Element && target.closest('button, a, input, select, textarea') !== null

/** Строка общей таблицы чеков: то, что есть у чека любого источника. Своё у источника — в `columns`. */
export interface ReceiptsTableRow {
  id: string
  refund: boolean
  /** Номер чека как у источника; пусто — «без номера». */
  number: string
  /** Время чека, уже в поясе отчёта. */
  at: string
  register: string
  cashier: string
  total: number | null
  cash: number | null
  card: number | null
  /** Чек не вошёл в выручку (не подтверждён, не проведён): строка приглушена, причина — в столбце источника. */
  muted?: boolean
}

/** Свой столбец источника (состояние, смена, источник магазина): короткий текст в одну строку. */
export interface ReceiptsTableColumn<T> {
  id: string
  label: string
  cell: (row: T) => ReactNode
  /** Полный текст ячейки для подсказки, когда он обрезан многоточием. */
  title?: (row: T) => string
}

/**
 * Список чеков — ОДНА таблица на всё приложение (статистика магазина, вкладки «Чеки» источников, отчёт кассира, своя касса):
 * строка в одну линию, без зазоров; длинное обрезается многоточием, полное — в подсказке; таблица прокручивается вбок. Нажатие на
 * строку (или Enter на ней) открывает окно чека — чек в списке не раскрывается. Приход и возврат — значком, словом и оттенком строки.
 */
export function ReceiptsTable<T extends ReceiptsTableRow>({ label, rows, columns = [], onOpen, focusId }: {
  label: string
  rows: readonly T[]
  columns?: readonly ReceiptsTableColumn<T>[]
  onOpen: (row: T) => void
  /** Чек, к которому прокрутить список (открытый по ссылке). */
  focusId?: string | null
}) {
  const keyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
    event.preventDefault()
    onOpen(row)
  }
  return <div className="krp-receipt-table-wrap">
    <table className="krp-receipts" aria-label={label}>
      <thead><tr>
        <th scope="col">Чек</th><th scope="col">Время</th><th scope="col">Касса</th><th scope="col">Кассир</th>
        {columns.map((column) => <th key={column.id} scope="col">{column.label}</th>)}
        <th scope="col">Оплата</th><th scope="col" className="krp-receipts__money">Сумма</th>
      </tr></thead>
      <tbody>{rows.map((row) => {
        const name = `${row.refund ? 'Возврат' : 'Приход'} № ${row.number || 'без номера'}`
        return <tr key={row.id} data-kind={row.refund ? 'refund' : 'sale'} data-muted={row.muted || undefined} tabIndex={0}
          aria-label={`Открыть: ${name}`} data-focus={row.id === focusId || undefined}
          ref={row.id === focusId ? (element) => element?.scrollIntoView({ block: 'center' }) : undefined}
          onClick={(event) => { if (!fromControl(event.target)) onOpen(row) }} onKeyDown={(event) => keyDown(event, row)}>
          <td title={name}><span className="krp-receipts__kind">
            {row.refund ? <Undo2 size={14} aria-hidden="true" /> : <ShoppingCart size={14} aria-hidden="true" />}{row.number || 'без номера'}
          </span></td>
          <td title={row.at}>{row.at}</td>
          <td title={row.register}>{row.register}</td>
          <td title={row.cashier}>{row.cashier}</td>
          {columns.map((column) => <td key={column.id} title={column.title?.(row)}>{column.cell(row)}</td>)}
          <td><ReceiptPaymentKindText cash={row.cash} card={row.card} /></td>
          <td className="krp-receipts__money">{money(row.total)}</td>
        </tr>
      })}</tbody>
    </table>
  </div>
}

/** Реквизит шапки чека: подпись слева, значение справа. */
export interface ReceiptFact { label: string; value: ReactNode }

/** Шапка окна чека — таблица «подпись — значение», без фраз. */
export function ReceiptFacts({ label, facts }: { label: string; facts: readonly ReceiptFact[] }) {
  return <table className="krp-receipt-facts" aria-label={label}>
    <tbody>{facts.map((fact) => <tr key={fact.label}><th scope="row">{fact.label}</th><td>{fact.value}</td></tr>)}</tbody>
  </table>
}

/**
 * Позиция чека. Фото и переход в карточку — только у позиции с ключом товара источника (`open` задан): по штрихкоду или имени
 * товар не угадывается (решение владельца 01.11.2026). Деньги и количества — уже текстом источника.
 */
export interface ReceiptLineRow {
  id: string
  name: string
  quantity: string
  price: string
  amount: string
  unit?: string | null
  discount?: string | null
  vat?: string | null
  barcode?: string | null
  /** Предмет расчёта, как записан в чеке. */
  subject?: string | null
  /** Сколько уже возвращено по позиции (у источника, который это знает). */
  returned?: string | null
  /** Миниатюра товара по ключу; `null` — фото нет (заглушка); не задано — у источника фото товаров нет, столбца фото нет. */
  photo?: string | null
  /** Открыть карточку товара источника — та же, что из номенклатуры. */
  open?: () => void
}

type Optional = 'unit' | 'discount' | 'vat' | 'subject' | 'barcode' | 'returned'
const OPTIONAL: readonly { key: Optional; label: string }[] = [
  { key: 'unit', label: 'Ед.' }, { key: 'discount', label: 'Скидка' }, { key: 'vat', label: 'НДС' }, { key: 'subject', label: 'Предмет' },
  { key: 'barcode', label: 'Штрихкод' }, { key: 'returned', label: 'Возвращено' },
]

/**
 * Товары чека — компактная таблица, как список номенклатуры: миниатюра и название-кнопка открывают ту же карточку товара. Столбец
 * показан, только если он заполнен хотя бы у одной позиции; ширина ограничена, длинное — многоточием, полное — в подсказке.
 */
export function ReceiptLinesTable({ lines }: { lines: readonly ReceiptLineRow[] }) {
  const optional = OPTIONAL.filter((column) => lines.some((line) => line[column.key] != null && line[column.key] !== ''))
  const thumbs = lines.some((line) => line.photo !== undefined)
  return <div className="krp-receipt-table-wrap">
    <table className="krp-receipt-lines" aria-label="Товары чека">
      <thead><tr>
        <th scope="col" className="krp-receipt-lines__no">№</th>
        {thumbs && <th scope="col"><span className="krp-receipt-lines__sr">Фото</span></th>}
        <th scope="col">Товар</th><th scope="col" className="krp-receipts__money">Кол-во</th>
        {optional.filter((column) => column.key === 'unit').map((column) => <th key={column.key} scope="col">{column.label}</th>)}
        <th scope="col" className="krp-receipts__money">Цена</th>
        {optional.filter((column) => column.key === 'discount').map((column) => <th key={column.key} scope="col" className="krp-receipts__money">{column.label}</th>)}
        <th scope="col" className="krp-receipts__money">Сумма</th>
        {optional.filter((column) => column.key !== 'unit' && column.key !== 'discount').map((column) => <th key={column.key} scope="col">{column.label}</th>)}
      </tr></thead>
      <tbody>{lines.map((line, index) => <tr key={line.id}>
        <td className="krp-receipt-lines__no">{index + 1}</td>
        {thumbs && <td className="krp-receipt-lines__thumb">{line.photo !== undefined && <ProductThumb src={line.photo} name={line.name} size={32} />}</td>}
        <td className="krp-receipt-lines__name" title={line.name}>
          {line.open === undefined ? line.name : <button type="button" className="krp-receipt-lines__open" onClick={line.open}>{line.name}</button>}
        </td>
        <td className="krp-receipts__money">{line.quantity}</td>
        {optional.filter((column) => column.key === 'unit').map((column) => <td key={column.key} title={line.unit ?? ''}>{line.unit ?? ''}</td>)}
        <td className="krp-receipts__money">{line.price}</td>
        {optional.filter((column) => column.key === 'discount').map((column) => <td key={column.key} className="krp-receipts__money">{line.discount ?? ''}</td>)}
        <td className="krp-receipts__money">{line.amount}</td>
        {optional.filter((column) => column.key !== 'unit' && column.key !== 'discount').map((column) =>
          <td key={column.key} title={line[column.key] ?? ''}>{line[column.key] ?? ''}</td>)}
      </tr>)}</tbody>
    </table>
  </div>
}
